import { FC, forwardRef } from 'react';
import {
  TBundle,
  TSession,
} from '@fe-learner-common/api/schemas/sessionsSchema';
import * as Dialog from '@radix-ui/react-dialog';
import { publishAnalyticsEvent } from '@utils/publishAnalyticsEvent';
import { cn, CourseImage, Modal, Tag } from '@fe-learner-common';
import { useIntl } from 'react-intl';
import { TW_ROOT } from '@utils/constants';
import { toLocaleDateTimeLong } from '@fe-learner-common/utils/date';
import { useSessionTranslations } from '@hooks/useSessionTranslations';
import { useSessionTags } from '@hooks/useSessionTags';
import { useBundleThumbnail, useSessionThumbnail } from '@hooks/useImageUrl';
import { Link } from 'react-router-dom';

type CourseCardProps = {
  bundle: TBundle;
  sessions?: TSession[] | null;
};

const Card: FC<CourseCardProps> = forwardRef<HTMLDivElement, CourseCardProps>(
  ({ bundle }, ref) => {
    const intl = useIntl();
    const thumbnail = useBundleThumbnail({ bundle });

    return (
      <div ref={ref} className="relative flex w-full flex-col gap-y-3">
        <CourseImage
          thumbnail={thumbnail}
          alt={intl.formatMessage({ id: 'courses.project.card.image.alt' })}
        />
        <div className="flex flex-col items-start">
          <div className="flex items-center">
            <h3 className="py-1.5 text-label-lg">{bundle.name}</h3>
          </div>
          <p className="flex gap-2 text-body-sm text-neutral-500">
            {intl.formatMessage({ id: 'catalog.collection' })}
            <Separator />
            {intl.formatMessage(
              {
                id: 'learner.common.nrOfCourses',
              },

              { nrOfCourses: bundle.sessions.length },
            )}
          </p>
        </div>
      </div>
    );
  },
);
Card.displayName = 'Card';

const Separator = () => <span>·</span>;
const CollectionCardListItem: FC<{ session: TSession }> = ({ session }) => {
  const texts = useSessionTranslations(session);
  const tags = useSessionTags(session);
  const thumbnail = useSessionThumbnail({ session, size: 144 });
  return (
    <li
      className={cn(
        'relative ',
        'after:absolute after:-bottom-1 after:left-4 after:right-4 after:z-[-1] after:box-content after:border-b-1 after:border-neutral-200 after:content-[""]',
        '[&:last-of-type]:after:border-none',
      )}
    >
      <Link
        key={`bundle-session-${session.uuid}`}
        className={cn(
          'l-btn-tertiary flex w-full items-start gap-5 rounded-lg p-4 text-neutral-900',
        )}
        to={`./session/${session.uuid}`}
        onClick={() => {
          publishAnalyticsEvent(
            {
              context: 'CatalogPage',
              action: 'CourseCardClicked',
            },
            {
              courseId: session?.uuid,
            },
          );
        }}
      >
        <CourseImage
          className="rounded-lg"
          width={72}
          alt={session.name || ''}
          thumbnail={thumbnail}
        />
        <div>
          <h3 className="pb-1 text-label-lg">{session.name}</h3>
          <div className="flex flex-wrap gap-2 text-body-md text-neutral-500">
            {session.startDate && (
              <>
                <span>{toLocaleDateTimeLong(session.startDate)}</span>
                <Separator />
              </>
            )}

            {session.location && <span>{session.location}</span>}
            {!session.location && session.format && <span>{texts.format}</span>}

            {tags
              ? Object.entries(tags).map(([key, value]) => (
                  <Tag
                    key={`tag-bundle-${session.uuid}-${key}`}
                    icon={value.icon}
                    type={value.type}
                  >
                    {value.text}
                  </Tag>
                ))
              : null}
          </div>
        </div>
      </Link>
    </li>
  );
};

export const CollectionCard: FC<CourseCardProps> = ({ bundle, sessions }) => {
  const intl = useIntl();
  const Trigger = () => (
    <Dialog.Trigger asChild>
      <button
        className={
          'flex h-full cursor-pointer flex-col gap-3 rounded-2xl p-3 hover:bg-neutral-100 sm:m-0 sm:size-auto'
        }
      >
        <Card bundle={bundle} />
      </button>
    </Dialog.Trigger>
  );

  const title = (
    <div>
      <p className="text-body-sm text-neutral-500">
        {intl.formatMessage({ id: 'catalog.collection' })}
      </p>

      <h1 className="text-title-md">{bundle.name}</h1>
    </div>
  );

  return (
    <Modal
      container={document.getElementById(TW_ROOT) as HTMLElement}
      className="md:w-[640px]"
      Trigger={Trigger}
      TitleContent={title}
    >
      <ul className="flex flex-col gap-y-2 p-2">
        {sessions?.map((session) => (
          <CollectionCardListItem
            key={`bundle-session-${session.uuid}`}
            session={session}
          />
        ))}
      </ul>
    </Modal>
  );
};
